/**
 * Offline queue for mutations that were attempted while the network was down.
 * Currently only used for POST /api/reports (segnalazioni offline).
 *
 * Storage: idb-keyval (single IndexedDB key holding an array of jobs).
 * Job shape:
 *   { id, type: 'report', payload: {...}, created_at: iso }
 */
import { get, set, del } from "idb-keyval";
import { api } from "@/api/client";

const KEY = "kilolava.offline.queue.v1";

async function readQueue() {
  return (await get(KEY)) || [];
}

async function writeQueue(items) {
  if (!items || items.length === 0) return del(KEY);
  return set(KEY, items);
}

export async function enqueueReport(payload) {
  return enqueueJob("report", payload);
}

/** Completamento pulizia fatto senza rete: viene rispedito appena torna il segnale. */
export async function enqueueCleaningComplete(cleaningId, payload) {
  return enqueueJob("cleaning_complete", { cleaning_id: cleaningId, ...payload });
}

/** Segnalazione/scostamento dichiarato senza rete. */
export async function enqueueDeviation(cleaningId, payload) {
  return enqueueJob("deviation", { cleaning_id: cleaningId, ...payload });
}

async function enqueueJob(type, payload) {
  const jobs = await readQueue();
  const job = {
    id: (crypto?.randomUUID && crypto.randomUUID()) || String(Date.now()) + Math.random(),
    type,
    payload,
    created_at: new Date().toISOString(),
  };
  jobs.push(job);
  await writeQueue(jobs);
  notifyListeners();
  return job;
}

export async function pendingCount() {
  return (await readQueue()).length;
}

let _flushing = false;
export async function flushQueue() {
  if (_flushing) return { flushed: 0, failed: 0 };
  _flushing = true;
  try {
    const jobs = await readQueue();
    if (jobs.length === 0) return { flushed: 0, failed: 0 };
    const remaining = [];
    let flushed = 0;
    let failed = 0;
    for (const j of jobs) {
      try {
        if (j.type === "report") {
          await api.post("/reports", j.payload);
          flushed += 1;
        } else if (j.type === "cleaning_complete") {
          const { cleaning_id: cid, ...body } = j.payload;
          // Il video può arrivare da una coda separata: se non è ancora collegato
          // il backend risponde 400, quindi il job resta in coda e si riprova.
          if (!body.video_file_id) {
            try {
              const { data: cur } = await api.get(`/cleanings/${cid}`);
              if (cur.status === "completata") {
                flushed += 1;
                await writeQueue((await readQueue()).filter((x) => x.id !== j.id));
                continue;
              }
              if (cur.video_file_id) body.video_file_id = cur.video_file_id;
            } catch { /* offline: gestito sotto */ }
          }
          await api.post(`/cleanings/${cid}/complete`, body);
          flushed += 1;
        } else if (j.type === "deviation") {
          const { cleaning_id: cid, ...body } = j.payload;
          await api.post(`/deviations/cleanings/${cid}`, body);
          flushed += 1;
        } else {
          // Unknown type — drop it silently so we don't get stuck
          flushed += 1;
        }
      } catch (e) {
        // Rete assente o completamento in attesa del video: il job resta in coda.
        const isNetwork = !e?.response;
        const waitingVideo = j.type === "cleaning_complete" && e?.response?.status === 400;
        if (isNetwork || waitingVideo) {
          remaining.push(j);
        }
        failed += 1;
      }
    }
    await writeQueue(remaining);
    notifyListeners();
    return { flushed, failed };
  } finally {
    _flushing = false;
  }
}

// Simple pub/sub so the banner updates when a job is added or drained.
const listeners = new Set();
export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
function notifyListeners() {
  for (const fn of listeners) {
    try { fn(); } catch (_) {}
  }
}

// Auto-flush when the browser goes back online.
if (typeof window !== "undefined") {
  window.addEventListener("online", () => { flushQueue(); });
}
